import { HeatmapLayer } from '@deck.gl/aggregation-layers/typed'
import { CoffeeShop } from '../../../hooks/useCoffeeShops'

interface CoffeeHeatmapLayerProps {
  data: CoffeeShop[]
  visible?: boolean
  radiusPixels?: number
  intensity?: number
  threshold?: number
  isPrewarming?: boolean
}

export function createCoffeeHeatmapLayer({
  data,
  visible = true,
  radiusPixels = 80, // Increased for better coverage
  intensity = 2, // Increased for more visible effect
  threshold = 0.01, // Lowered to show more subtle patterns
  isPrewarming = false
}: CoffeeHeatmapLayerProps): HeatmapLayer {
  // Create unique ID to prevent conflicts
  const layerId = isPrewarming ? 'coffee-heatmap-prewarm' : 'coffee-heatmap'

  return new HeatmapLayer({
    id: layerId,
    data,
    getPosition: (d: CoffeeShop) => [d.lon, d.lat],
    getWeight: () => 1, // Each coffee shop has equal weight
    radiusPixels,
    intensity,
    threshold,
    visible,
    pickable: false,
    // CRITICAL: Proper updateTriggers for deck.gl performance
    updateTriggers: {
      getPosition: data, // Only update when data reference changes
      getWeight: 1 // Weight is constant, never update
    },
    // Optimize texture size for performance
    textureSize: 512, // Fixed size for consistent performance
    // Enhanced color scheme for better visibility
    colorRange: [
      [255, 255, 204, 40],   // Very light yellow (low density) - more visible
      [255, 237, 160, 80],   // Light yellow
      [254, 217, 118, 120],  // Yellow-orange
      [254, 178, 76, 160],   // Orange
      [253, 141, 60, 200],   // Dark orange
      [240, 59, 32, 240],    // Red-orange
      [189, 0, 38, 255]      // Dark red (high density)
    ],
    // Aggregation parameters
    aggregation: 'SUM',
    // Optimized settings for consistent performance
    weightsTextureSize: 1024, // Balances performance and visual quality: smaller than default 2048 for better performance, larger than color texture (512) for adequate detail resolution
    debounceTimeout: 100 // Moderate debouncing
  })
}

export default createCoffeeHeatmapLayer
